import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const SEEN_KEY = "ppgf_welcome_seen";

export const WelcomePopup = () => {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(SEEN_KEY) || sessionStorage.getItem("b2b_token")) return;
    if (window.location.pathname.startsWith("/admin")) return;
    const t = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    localStorage.setItem(SEEN_KEY, "1");
    setVisible(false);
  };

  const subscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/newsletter/subscribe", { email });
      setSub(r.data);
      localStorage.setItem(SEEN_KEY, "1");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore, riprova");
    }
    setLoading(false);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(sub.code);
    toast.success("Codice copiato!");
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="welcome-popup"
            className="relative w-full max-w-md rounded-[2rem] bg-[#FDFBF7] border border-[#E7E0D8] p-8 sm:p-10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button data-testid="welcome-close-btn" onClick={close} aria-label="Chiudi" className="absolute top-5 right-5 text-[#78716C] hover:text-[#1C1917] transition-colors">
              <X className="w-5 h-5" />
            </button>
            <div className="w-12 h-12 rounded-full bg-[#2E8A57]/10 flex items-center justify-center mb-5">
              <Gift className="w-5 h-5 text-[#2E8A57]" />
            </div>
            {sub ? (
              <div data-testid="welcome-success">
                <h3 className="font-serif-display font-bold text-2xl text-[#1C1917]">Ecco il tuo sconto!</h3>
                <p className="mt-2 text-sm text-[#57534E]">Usa questo codice al checkout per il {sub.discount_percent}% di sconto sul tuo primo ordine:</p>
                <button data-testid="welcome-code" onClick={copyCode} className="mt-5 w-full flex items-center justify-center gap-3 rounded-xl border-2 border-dashed border-[#7DC242] bg-[#7DC242]/10 py-4 font-mono-accent font-bold text-xl text-[#2E8A57] tracking-widest hover:bg-[#7DC242]/20 transition-colors">
                  {sub.code} <Copy className="w-4 h-4" />
                </button>
                <p className="mt-3 text-xs text-[#78716C] text-center">Te l'abbiamo inviato anche via email · valido una volta</p>
                <button data-testid="welcome-continue-btn" onClick={close} className="btn-tuscan w-full mt-5 py-3.5 rounded-full text-sm font-semibold">Inizia a ordinare</button>
              </div>
            ) : (
              <>
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#2E8A57] mb-2">Benvenuto</p>
                <h3 className="font-serif-display font-bold text-2xl sm:text-3xl text-[#1C1917] leading-tight">
                  Il 10% di sconto sul tuo primo ordine
                </h3>
                <p className="mt-3 text-sm text-[#57534E] leading-relaxed">Lascia la tua email e ricevi subito un codice sconto da usare al checkout.</p>
                <form onSubmit={subscribe} className="mt-6 space-y-3">
                  <input
                    data-testid="welcome-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="La tua email"
                    className="w-full rounded-xl border border-[#E7E0D8] bg-white px-4 py-3.5 text-sm focus:outline-none focus:border-[#2E8A57] transition-colors"
                  />
                  <button data-testid="welcome-submit-btn" type="submit" disabled={loading} className="btn-tuscan w-full py-4 rounded-full text-sm font-semibold disabled:opacity-50">
                    {loading ? "Invio..." : "Ottieni il -10%"}
                  </button>
                </form>
                <button data-testid="welcome-dismiss-btn" onClick={close} className="mt-4 w-full text-center text-xs text-[#78716C] hover:text-[#1C1917] underline transition-colors">
                  No grazie, continua senza sconto
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
